import { Ds, SearchSelect } from "@accurx/design";
import {
    ChooseClinicianValue,
    ClinicianAvailabilityOption,
} from "domains/self-book/utils/types";

import { TempFontWrapper } from "../SelfbookConfigurationForm.styles";

type ChooseClinicianProps = {
    options: ClinicianAvailabilityOption[];
    initialOption:
        | {
              clinicians: ClinicianAvailabilityOption[];
              clinicianType: ChooseClinicianValue;
          }
        | undefined;
    onChange: (
        selected: ClinicianAvailabilityOption[] | undefined,
        chooseClinicianType: ChooseClinicianValue,
    ) => void;
    errors: string[] | undefined;
    showLoading?: boolean;
};

type ChooseClinicianOption = {
    id: string;
    label: string;
    value: ChooseClinicianValue;
};

const radioInputs: ChooseClinicianOption[] = [
    { id: "anyClinician", label: "Any clinician", value: "AnyClinician" },
    {
        id: "specificClinicians",
        label: "Specific clinicians",
        value: "SpecificClinicians",
    },
];

export const ChooseClinician = ({
    options,
    initialOption,
    onChange,
    errors,
    showLoading,
}: ChooseClinicianProps) => {
    const label = "Clinicians available for booking";
    const id = "choose-clinician-filter";

    const onChooseClinicianTypeChange = (value: string) => {
        const clinicianType = value as ChooseClinicianValue;
        onChange(initialOption?.clinicians, clinicianType);
    };

    return (
        <Ds.Box pt="1.5rem">
            <Ds.Fieldset name={id}>
                <Ds.Legend>{label}</Ds.Legend>
                <Ds.RadioGroup
                    name="choose-clinician-type"
                    defaultValue={initialOption?.clinicianType}
                    value={initialOption?.clinicianType}
                    onValueChange={(value) => {
                        onChooseClinicianTypeChange(value);
                    }}
                    appearance="ghost"
                >
                    <Ds.Flex flexDirection="column">
                        {radioInputs.map((item) => (
                            <Ds.RadioGroup.Item
                                value={item.value}
                                key={item.value}
                            >
                                <Ds.RadioGroup.Indicator />
                                <Ds.RadioGroup.Label>
                                    {item.label}
                                </Ds.RadioGroup.Label>
                            </Ds.RadioGroup.Item>
                        ))}
                    </Ds.Flex>
                </Ds.RadioGroup>

                {initialOption?.clinicianType === "SpecificClinicians" && (
                    <TempFontWrapper>
                        <SearchSelect
                            id={id}
                            key={initialOption.clinicians
                                .flatMap((item) => item.count)
                                .toString()}
                            label={label}
                            options={options}
                            dimension="medium"
                            handleCallbacks={{
                                onClearAll: () => {
                                    onChange([], initialOption.clinicianType);
                                },
                                onCollapse: (selected) =>
                                    onChange(
                                        selected as ClinicianAvailabilityOption[],
                                        initialOption.clinicianType,
                                    ),
                                onRemove: (selected) => {
                                    onChange(
                                        selected as ClinicianAvailabilityOption[],
                                        initialOption.clinicianType,
                                    );
                                },
                            }}
                            defaultCopy={{
                                placeholder: "Search for a name",
                            }}
                            initSelectedValues={initialOption.clinicians}
                            error={errors && errors.length > 0}
                            theme="MULTISELECT"
                            showLoading={showLoading}
                        />
                    </TempFontWrapper>
                )}
                {errors &&
                    errors.map((item) => (
                        <Ds.Form.Error key={item}>{item}</Ds.Form.Error>
                    ))}
            </Ds.Fieldset>
        </Ds.Box>
    );
};
